import { useEffect, useState } from "react";
import PageHeader from "../components/layout/PageHeader";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "../components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

const EMPTY = { game_id: "", play_type_id: "", number: "", branch_id: "", limit_amount: "" };

export default function NumberLimits() {
  const [items, setItems] = useState([]);
  const [games, setGames] = useState([]);
  const [playTypes, setPlayTypes] = useState([]);
  const [branches, setBranches] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [filterGame, setFilterGame] = useState("all");

  const load = async () => {
    const params = filterGame !== "all" ? { game_id: filterGame } : {};
    const [l, g, p, b] = await Promise.all([
      api.get("/number-limits", { params }),
      api.get("/games"),
      api.get("/play-types"),
      api.get("/branches"),
    ]);
    setItems(l.data); setGames(g.data); setPlayTypes(p.data); setBranches(b.data);
  };
  useEffect(() => { load(); }, [filterGame]); // eslint-disable-line

  const save = async () => {
    try {
      await api.post("/number-limits", {
        ...form,
        branch_id: form.branch_id || null,
        limit_amount: parseFloat(form.limit_amount),
      });
      toast.success("Límite creado");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (l) => {
    if (!window.confirm(`Eliminar límite del número ${l.number}?`)) return;
    try { await api.delete(`/number-limits/${l.id}`); toast.success("Eliminado"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="flex-1 bg-slate-50 min-h-full">
      <PageHeader
        title="Límites de Números"
        subtitle="Bloqueo automático al alcanzar la exposición máxima"
        testId="limits-header"
        actions={
          <Button onClick={() => { setForm(EMPTY); setOpen(true); }} className="bg-blue-600 hover:bg-blue-700 rounded-sm font-bold" data-testid="limits-create-btn">
            <Plus className="w-4 h-4 mr-2" /> Nuevo límite
          </Button>
        }
      />
      <div className="p-6 sm:p-8 space-y-4">
        <div className="flex gap-2 max-w-xs">
          <Select value={filterGame} onValueChange={setFilterGame}>
            <SelectTrigger className="rounded-sm" data-testid="limits-filter-game"><SelectValue placeholder="Filtrar por juego" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los juegos</SelectItem>
              {games.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">
                <Th>Número</Th><Th>Juego</Th><Th>Tipo</Th><Th>Sucursal</Th><Th>Límite</Th><Th></Th>
              </tr>
            </thead>
            <tbody>
              {items.map((l) => {
                const g = games.find(x => x.id === l.game_id);
                const p = playTypes.find(x => x.id === l.play_type_id);
                const b = branches.find(x => x.id === l.branch_id);
                return (
                  <tr key={l.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`limit-row-${l.number}`}>
                    <td className="p-3 font-mono font-black text-xl text-slate-950">{l.number}</td>
                    <td className="p-3 text-slate-900">{g?.name || "—"}</td>
                    <td className="p-3 text-slate-700">{p?.name || "—"}</td>
                    <td className="p-3 text-slate-700">{b ? `${b.code}` : <span className="text-slate-400">Global</span>}</td>
                    <td className="p-3 font-mono font-bold text-right text-slate-950">RD${l.limit_amount.toFixed(2)}</td>
                    <td className="p-3 text-right">
                      <Button size="sm" variant="outline" onClick={() => remove(l)} className="h-8 rounded-sm border-red-300 text-red-700"><Trash2 className="w-3 h-3" /></Button>
                    </td>
                  </tr>
                );
              })}
              {items.length === 0 && <tr><td colSpan="6" className="p-8 text-center text-slate-500 text-sm">Sin límites configurados</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md rounded-sm">
          <DialogHeader><DialogTitle>Nuevo límite de número</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Juego</Label>
              <Select value={form.game_id} onValueChange={(v) => setForm({ ...form, game_id: v })}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="limit-form-game"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                <SelectContent>{games.map(g => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Tipo de jugada</Label>
              <Select value={form.play_type_id} onValueChange={(v) => setForm({ ...form, play_type_id: v })}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="limit-form-playtype"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                <SelectContent>{playTypes.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider">Número</Label>
                <Input value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value.replace(/\D/g, "") })} className="mt-1 rounded-sm font-mono text-lg text-center" data-testid="limit-form-number" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider">Monto límite (RD$)</Label>
                <Input type="number" step="0.01" value={form.limit_amount} onChange={(e) => setForm({ ...form, limit_amount: e.target.value })} className="mt-1 rounded-sm font-mono" data-testid="limit-form-amount" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Sucursal (opcional)</Label>
              <Select value={form.branch_id || "global"} onValueChange={(v) => setForm({ ...form, branch_id: v === "global" ? "" : v })}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="limit-form-branch"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="global">Global (todas las sucursales)</SelectItem>
                  {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.code} · {b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancelar</Button>
            <Button onClick={save} className="bg-blue-600 hover:bg-blue-700 rounded-sm" data-testid="limit-form-save">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Th = ({ children }) => <th className="p-3 text-xs uppercase tracking-wider font-bold text-slate-600">{children}</th>;
